import { useState } from "react";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useRefresh } from "@/hooks/useRefresh";
import { peso, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Download } from "lucide-react";

const KINDS = [
  { v: "stock", l: "Stock on hand" },
  { v: "movements", l: "Movements" },
  { v: "wastage", l: "Wastage" },
  { v: "purchases", l: "Purchases by supplier" },
  { v: "vat", l: "VAT summary" },
  { v: "variance", l: "Count variance" },
];
const MONEY = ["value", "net", "vat", "gross", "net_cost", "value_impact"];

const manilaToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
const shiftDays = (dateStr, days) => {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

function presetRange(preset) {
  const today = manilaToday();
  if (preset === "today") return [today, today];
  if (preset === "7d") return [shiftDays(today, -6), today];
  if (preset === "week") {
    const dow = (new Date(today + "T00:00:00").getDay() + 6) % 7; // Monday start
    return [shiftDays(today, -dow), today];
  }
  if (preset === "month") return [today.slice(0, 8) + "01", today];
  if (preset === "lastmonth") {
    const first = new Date(today.slice(0, 8) + "01T00:00:00");
    first.setMonth(first.getMonth() - 1);
    const from = first.toISOString().slice(0, 10);
    return [from, shiftDays(today.slice(0, 8) + "01", -1)];
  }
  return [null, null];
}

const PRESETS = [
  { v: "all", l: "All time" }, { v: "today", l: "Today" }, { v: "week", l: "This week" },
  { v: "7d", l: "Last 7 days" }, { v: "month", l: "This month" }, { v: "lastmonth", l: "Last month" },
  { v: "custom", l: "Custom range" },
];

export default function Reports() {
  const { selected } = useBranch();
  const [kind, setKind] = useState("stock");
  const [preset, setPreset] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState({ rows: [], headers: [] });

  const dateParams = () => {
    if (kind === "stock") return {};
    if (preset === "custom") return { date_from: custom.from || undefined, date_to: custom.to || undefined };
    const [from, to] = presetRange(preset);
    return { date_from: from || undefined, date_to: to || undefined };
  };

  useRefresh(() => {
    api.get(`/reports/${kind}`, { params: { branch_id: selected, ...dateParams() } }).then((r) => setData(r.data));
  }, [kind, selected, preset, custom.from, custom.to]);

  const exportCsv = async () => {
    const res = await api.get(`/reports/${kind}`, { params: { branch_id: selected, format: "csv", ...dateParams() }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (h, v) => {
    if (v === null || v === undefined) return "—";
    if (MONEY.includes(h)) return peso(v);
    if (h === "created_at") return fmtDate(v);
    return String(v);
  };

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Reports</h1>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="flex-wrap h-auto">
          {KINDS.map((k) => <TabsTrigger key={k.v} value={k.v} data-testid={`report-tab-${k.v}`}>{k.l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {kind === "stock" ? (
        <p className="text-xs text-muted-foreground">Stock on hand is a current snapshot — date range doesn't apply.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-44 bg-white" data-testid="report-date-preset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <>
              <Input type="date" className="w-40 bg-white" value={custom.from}
                onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="report-date-from" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input type="date" className="w-40 bg-white" value={custom.to}
                onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="report-date-to" />
            </>
          )}
          <span className="text-xs text-muted-foreground">{data.rows.length} rows · dates in Asia/Manila</span>
        </div>
      )}
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              {data.headers.map((h) => <th key={h} className="py-2 px-3 whitespace-nowrap">{h.replace(/_/g, " ")}</th>)}
            </tr>
          </thead>
          <tbody data-testid="report-table-body">
            {data.rows.length === 0 && <tr><td colSpan={data.headers.length} className="py-6 text-center text-muted-foreground">No data</td></tr>}
            {data.rows.slice(0, 300).map((r, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                {data.headers.map((h) => (
                  <td key={h} className={`py-2 px-3 whitespace-nowrap ${MONEY.includes(h) ? "text-right" : ""}`}>{fmt(h, r[h])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.rows.length > 300 && <p className="text-xs text-muted-foreground">Showing first 300 rows — export CSV for the full report.</p>}
    </div>
  );
}
